import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";

import { useTalentSkills } from "../../api/talentSkills";
import Button from "../../components/Button";
import SkillsSearchDropdown from "../../components/SkillsSearchDropdown";
import { GigApiFetcherResponse } from "../../api/common/fetching";

import TalentSkillChips from "./TalentSkillChips";
import { TalentSkillsFormValues, validationSchemaFields } from "./TalentSkillsFormValues";
import { talentProfileEditTooltips } from "../../tooltipsContent";
import { useOrganizationContext } from "../../api/current-organization/organizationContext";
import ErrorList from "../../components/ErrorList";
import { useTalentPersonalInformationContext } from "../../api/talentPersonalInformation";

const validationSchema = yup.object(validationSchemaFields);

const TalentSkillsForm = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const { addTalentSkill, removeTalentSkill, talentSkills, isRemovingTalentSkill } = useTalentSkills();
    const { gigTerminology } = useOrganizationContext();
    const { reload: reloadPersonalInformation } = useTalentPersonalInformationContext();

    const handleRemoveSkill = async (skillId: string): Promise<GigApiFetcherResponse<void>> => {
        const response = await removeTalentSkill(skillId);

        if (response.success && talentSkills.length <= 3) {
            reloadPersonalInformation();
        }

        return response;
    };

    const handleSubmit = async (values: TalentSkillsFormValues) => {
        setErrors([]);

        const existingSkill = talentSkills.find(_ => _.skillId === values.skillId);

        if (existingSkill) {
            setErrors([`Workshop type "${existingSkill.skillName}" already added.`]);
            methods.reset();
            return;
        }

        setIsSubmitting(true);

        const response = await addTalentSkill(values.skillId);

        setIsSubmitting(false);

        if (!response.success) {
            setErrors(response.error.userMessages);
        } else {
            methods.reset();
            if (talentSkills.length >= 2) reloadPersonalInformation();
        }
    };

    const methods = useForm<TalentSkillsFormValues>({
        resolver: yupResolver(validationSchema)
    });

    return (
        <div>
            <form
                onSubmit={methods.handleSubmit(handleSubmit)}
                noValidate
                className="flex gap-6 flex-wrap md:flex-nowrap md:items-start"
            >
                <div className="flex gap-6 flex-wrap w-full md:flex-nowrap">
                    <div className="w-full">
                        <Controller
                            name="skillId"
                            control={methods.control}
                            render={({ field: { onChange, value } }) => (
                                <SkillsSearchDropdown
                                    required
                                    tooltip={talentProfileEditTooltips.addSkills(gigTerminology)}
                                    value={value}
                                    disabled={isSubmitting}
                                    onChange={onChange}
                                    error={methods.formState.errors.skillId}
                                />
                            )}
                        />
                    </div>
                </div>
                <Button
                    className="w-full md:mt-auto md:w-auto"
                    loading={isSubmitting}
                    variant="secondary"
                    type="submit"
                >
                    Add workshop type
                </Button>
            </form>

            <ErrorList errors={errors} />

            <TalentSkillChips skills={talentSkills} removeSkill={handleRemoveSkill} isRemovingSkill={isRemovingTalentSkill} />
            
        </div>
    );
};

export default TalentSkillsForm;